import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import { TaskStatusType } from '@/app/enums/TaskStatusType';
import { TaskType } from '@/app/enums/TaskType';
import { type TaskInfoDto } from '@/app/models/TaskInfoDto';
import TaskReadyToast from '../components/TaskReadyToast';

type Task = TaskInfoDto<string>;

/** Nome com que o backend cria a tarefa de exportação, antes de conhecer o título do documento. */
const UNTITLED_EXPORT = 'Exportação';

const AUTOMATION_TASK_LABELS: Partial<Record<TaskType, string>> = {
  [TaskType.AUTOMATIC_REPLENISHMENT]: 'Reposição automática',
  [TaskType.AUTOMATIC_PICKLIST]: 'Picklist automática',
};

const automationTaskLabelOf = (type?: TaskType) => (type ? AUTOMATION_TASK_LABELS[type] : undefined);

export const isAutomationTask = (type?: TaskType) => automationTaskLabelOf(type) !== undefined;

export const notifyTaskFinished = (task: Task) => {
  const automationLabel = automationTaskLabelOf(task.type);
  if (automationLabel) {
    const taskTitle = task.title ?? task.name;
    if (task.status === TaskStatusType.COMPLETED) {
      SystemNotification.success(`${automationLabel} concluída: ${taskTitle}`);
      return;
    }
    const reason = task.error ? `: ${task.error}` : '';
    const automationName = automationLabel.toLowerCase();
    SystemNotification.error(`A ${automationName} ${taskTitle} falhou${reason}`);
    return;
  }

  if (task.status === TaskStatusType.COMPLETED) {
    // Posição padrão dos toasts do app (superior direito): no inferior direito ele cobria a bolha do chat.
    SystemNotification.custom(<TaskReadyToast task={task} />, {
      autoClose: 8000,
      icon: false,
      closeOnClick: false,
    });
    return;
  }

  // Se a consulta nem rodou, a tarefa ainda tem o nome com que o backend a cria, e não o da tela.
  const subject = task.title && task.title !== UNTITLED_EXPORT ? ` de ${task.title}` : '';
  const reason = task.error ? `: ${task.error}` : '';
  SystemNotification.error(`A exportação${subject} falhou${reason}`);
};

const SEEN_UNTIL_KEY = 'easypdv.tasks.seenUntil';

export const isRunning = (task: Task) => task.status === TaskStatusType.RUNNING;

/** Troca a tarefa de mesmo nome pela versão nova; tarefa inédita entra no topo (a lista é mais recentes primeiro). */
export const upsertTask = (tasks: Task[] | undefined, task: Task): Task[] => {
  const current = tasks ?? [];
  return current.some(({ name }) => name === task.name)
    ? current.map((existing) => (existing.name === task.name ? task : existing))
    : [task, ...current];
};

/** Média do progresso das tarefas em andamento, 0 a 1: é o anel do sino. */
export const averageProgress = (tasks: Task[]) => {
  const running = tasks.filter(isRunning);
  return running.length ? running.reduce((sum, task) => sum + (task.progress ?? 0), 0) / running.length : 0;
};

const endedAt = (task: Task) => (isRunning(task) || !task.end_time ? 0 : Date.parse(task.end_time));

/**
 * "Visto até" é o `end_time` da finalizada mais recente que a aba Tarefas já mostrou. Compara só relógio do
 * servidor com relógio do servidor, então a hora errada do navegador não cria nem esconde novidade.
 */
export const latestEndTime = (tasks: Task[]) => Math.max(0, ...tasks.map(endedAt));

export const countUnseen = (tasks: Task[], seenUntil: number) =>
  tasks.filter((task) => endedAt(task) > seenUntil).length;

/** Conveniência por navegador, não estado compartilhado. Armazenamento bloqueado não pode derrubar o header. */
export const readSeenUntil = () => {
  try {
    return Number(localStorage.getItem(SEEN_UNTIL_KEY)) || 0;
  } catch {
    return 0;
  }
};

export const writeSeenUntil = (seenUntil: number) => {
  try {
    localStorage.setItem(SEEN_UNTIL_KEY, String(seenUntil));
  } catch {
    // Sem armazenamento o "visto" vale só até recarregar.
  }
};

export const TASK_SENT_EVENT = 'easypdv:task-sent';

/**
 * Avisa o sino de que uma tarefa acabou de ir para segundo plano. Quem envia está dentro de uma tela e o sino
 * está no header: um evento de janela liga os dois sem contexto compartilhado. `from` é de onde o chip parte.
 */
export const announceTaskSent = (from?: DOMRect) =>
  globalThis.dispatchEvent(new CustomEvent<DOMRect | undefined>(TASK_SENT_EVENT, { detail: from }));

/** Tarefas que um modal aberto está esperando: a conclusão é dele (baixa e fecha), não do aviso do canto. */
const awaitedByModal = new Set<string>();

export const awaitTaskInModal = (name: string) => {
  awaitedByModal.add(name);
  return () => {
    awaitedByModal.delete(name);
  };
};

export const isAwaitedByModal = (name: string) => awaitedByModal.has(name);
